<template>
  <div :id="`${activityIdConcat}`" class="activity__item" :class="isURLActive && 'active'">
    <div class="activity__item-content">
      <!-- user  -->
      <span class="activity__item-user">
        <UserAvatar :tooltip="false" :user="activity.user" />
      </span>
      <div class="activity__item-info">
        <!-- action  -->
        <div class="activity__item-action">
          <span class="activity__item-creator">
            {{ userFullName }}
          </span>
          <!-- type comment -->
          <span v-if="activity.type === 'comment'" class="activity__item-decription">
            {{ $t('card_modal.ui.activity_comment_action') }}
            <AppIcon :icon="actionIcon" />
          </span>
          <!-- else: other actions  -->
          <template v-else>
            <span v-markdown="activity.title" class="activity__item-description-title"></span>
            <AppIcon :icon="actionIcon" />
          </template>
        </div>
        <!-- date & link  -->
        <span class="activity__item-date">
          <a :href="`${activityLink}`" class="activity__item-link">
            {{ $d(new Date(activity.created), 'long', currentUser.language) }}H
          </a>
        </span>
      </div>
      <!-- comment body -->
      <div
        v-if="isCommentType && !showCommentBox"
        class="activity__item-text"
        v-markdown="activity.message"
      ></div>
      <!-- comment box ( editor) -->
      <template v-if="isCommentType">
        <CommentBox
          v-if="showCommentBox"
          v-model="commentDraft"
          @save-comment="updateComment"
          @blur="toggleCommentEditor"
          compact
        />
        <!-- item options -->
        <CardModalActivityItemOptions
          v-if="isUserComment && !showCommentBox && isCardActive"
          @option-requested="optionRedirect"
        />
      </template>
    </div>
  </div>
</template>

<script>
// Utilities
import { mapState } from 'vuex'
import AlertIconType from 'Mixins/AlertIconType'
import { formatTitle, checkApiResponse, viewportIntersect } from 'Utils'
// Components
import AppIcon from 'Ui/AppIcon'
import CommentBox from 'Components/CommentBox'
import UserAvatar from 'Components/UserAvatar'
import CardModalActivityItemOptions from './CardModalActivityItemOptions'

export default {
  name: 'CardModalActivityItem',

  components: {
    AppIcon,
    UserAvatar,
    CommentBox,
    CardModalActivityItemOptions,
  },

  props: {
    activity: {
      type: Object,
      required: true,
    },
  },

  mixins: [AlertIconType],

  data() {
    return {
      timeout: null,
      showCommentBox: false,
      commentDraft: this.activity.message,
      modal: document.querySelector('.window-modal'),
    }
  },

  methods: {
    toggleCommentEditor() {
      this.showCommentBox = !this.showCommentBox
    },

    optionRedirect(option) {
      if (option === 'edit') this.toggleCommentEditor()
      if (option === 'delete') this.deleteComment()
    },

    async updateComment(comment) {
      this.commentDraft = comment

      const commentData = {
        cardId: this.$route.params.card_id,
        activityId: this.activity.id,
        comment,
      }

      const updatedComment = await this.$store.dispatch('cards/updateComment', commentData)
      if (!checkApiResponse(updatedComment)) return
      this.toggleCommentEditor()
      return updatedComment
    },

    async deleteComment() {
      const commentData = {
        cardId: this.$route.params.card_id,
        activityId: this.activity.id,
      }
      await this.$store.dispatch('cards/deleteComment', commentData)
    },

    sendAlertUpdate(intersections) {
      for (const intersection of intersections) {
        // is in viewport?
        if (!intersection.isIntersecting) return

        const activityAlert = this.$store.getters['alerts/getActivityAlert'](this.activity.id)
        if (!activityAlert?.is_active) return

        const updateInfo = {
          id: activityAlert.id,
          status: {
            is_active: false,
          },
        }

        this.$store.dispatch('alerts/update', updateInfo)
      }
    },
  },

  computed: {
    ...mapState({
      diyUsers: (state) => state.user.diyUsers,
      currentUser: (state) => state.user.currentUser,
      currentCard: (state) => state.cards.currentCard,
    }),

    activityIdConcat() {
      return this.activity.type
        ? `${this.activity.type}-${this.activity.id}`
        : `action-${this.activity.id}`
    },

    activityLink() {
      const cardTitle = formatTitle(this.currentCard.title)

      return `/account/${this.currentCard.account.id}/c/${this.currentCard.id}/${cardTitle}#${this.activityIdConcat}`
    },

    isCardActive() {
      return this.currentCard.status === 'active'
    },

    isURLActive() {
      return this.$route.hash.replace('#', '') === this.activityIdConcat
    },

    isCommentType() {
      return this.activity.type === 'comment'
    },

    isUserComment() {
      return this.currentUser.id == this.activity.user.id
    },

    userFullName() {
      return `${this.activity.user.first_name} ${this.activity.user.last_name}`
    },
  },

  watch: {
    async showCommentBox(newValue) {
      if (newValue) {
        await this.$nextTick()
        this.$el.querySelector('.comment-box .app-textarea').focus()
      }
    },
  },

  mounted() {
    viewportIntersect(this.sendAlertUpdate, this.$el)
  },
}
</script>

<style scoped>
.activity__item {
  display: inline-block;
  width: calc(100% + 20px);
  min-height: 32px;
  border-radius: 6px;
  padding: 0 10px;
  margin-left: -10px;
}

.activity__item:not(:first-of-type) {
  margin-top: 10px;
}

.activity__item.active,
.activity__item.unread {
  background-color: var(--c-secondary-400);
}

.activity__item-content,
.activity__item-user {
  display: flex;
  justify-items: flex-start;
  gap: 5px;
}

.activity__item-content {
  position: relative;
  flex-flow: row wrap;
  padding: 10px 0;
  padding-left: 38px;
}

.activity__item-user,
.activity__item-action {
  font-size: 14px;
  line-height: 18px;
  word-break: break-word;
}

.activity__item-user {
  position: absolute;
  top: 5px;
  left: 0;
  height: 32px;
  width: 32px;
}

.activity__item-info {
  display: flex;
  flex-flow: row nowrap;
  gap: 9px;
}

.activity__item-creator {
  font-weight: 700;
  color: var(--c-primary);
}

.activity__item-action {
  align-self: center;
  color: var(--c-secondary);
}

.activity__item-decription .app-icon {
  margin-left: 0.3125rem;
}

.activity__item-description-title.marked-text >>> * {
  display: inline;
  color: var(--c-secondary);
}

.activity__item-link {
  display: block;
  font-size: 10px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  color: var(--c-primary);
  border-radius: 2px;
  background-color: var(--c-secondary-500);
  padding: 4px 5px;
  white-space: nowrap;
  text-transform: uppercase;
  text-decoration: none;
}

.activity__item-link:hover {
  text-decoration: underline;
}

.activity__item-text {
  font-size: 14px;
  line-height: 20px;
  color: var(--c-primary);
  border-radius: 4px;
  border: 1px solid var(--c-secondary-500);
  background-color: var(--c-secondary-200);
  padding: 15px 20px;
  width: 100%;
  box-shadow: 0 1px 2px -1px var(--rgba--light-blue-300);
}

.activity__item-text,
.activity__item .comment-box {
  margin-top: 10px;
}

.active .activity__item-text,
.unread .activity__item-text {
  border-width: 2px;
  border-color: var(--c-magenta-500);
}
</style>
